/** IPlan (Public plan, visible by users) needs only specific props from IPlanModel,
 * to decide: do we need to combine them?
 */
import { INSTALLMENTS_BREAKDOWN_TYPES } from "../pages/manage-subscription-plans/stores/installment-list-manager";
import { IPlanEditorModelChanges, PLAN_CHARGE_TYPES } from "../pages/manage-subscription-plans/stores/subscription-plan-editor-store";
import {IAddon} from "./Addon";

export interface ICurrentUserPlan {
    plan: IPlan,
    isOnTrial: boolean,
    childStoresCount: number
}

/**TO BE DEPRECATED RD-478 - some fields on this interface should be deprecated, like parent_plan_features, plan_features_with_addons ... */
export interface IPlan {
    id: number,
    type: string,
    period?: number,
    name: string,
    internal_name: string,
    price: number,
    rma_quota: string,
    price_per_extra_rma?: number,
    tag: string,
    tag_color: string,
    plan_description: string,
    is_custom: boolean,
    deprecated: boolean,
    capped_amount: number,
    user_addons?: IAddon[],
    addons?: IAddon[],
    parent_plan?: IPlan,
    installments?: IInstallment[],
    features: IPlanFeature[],
    price_with_addons: number,
    plan_features_with_addons: IPlanFeature[],
    parent_plan_features: IPlanFeature[],
    parent_plan_id: number,
    updated_at: string,
    trial_days: number,
    plan_type: string,
    short_description: string,
    partner_id?: string,
    is_default_coverage_plan: boolean,
}

export enum PLAN_EDITOR_EVENTS {
    CREATE = 'create',
    UPDATE = 'update',
    ASSIGN = 'assign'
}

export interface IPlanEditorEventStory {
    id: number,
    user_id: number,
    user_email: string,
    system_plan_id?: number,
    regular_plan_id: number,
    action: PLAN_EDITOR_EVENTS,
    assigned_merchant?: number,
    created_at: string,
    updated_at: string
}

export interface IInstallment {
    id: number,
    days_since_last_installment: number|null,
    months_since_last_installment: number|null,
    absolute_charge_date: string|null,
    charge_amount: number;
    _deleted?: boolean;
}

/** Admin plan model, visible only by admins */
export interface IPlanEditorModel {
    id: number,
    type: PLAN_CHARGE_TYPES,
    period?: number,
    name: string,
    price?: number,
    capped_amount?: number,
    terms?: string,
    trial_days?: number,
    test: boolean,
    on_install: boolean,
    created_at?: string,
    updated_at?: string,
    rma_quota?: number,
    price_per_extra_rma?: number,
    tag?: string,
    tag_color?: string,
    internal_name?: string,
    is_custom: boolean,
    deprecated: boolean,
    plan_description: string,
    assigned_stores: number,
    short_description: string,
    features: IPlanFeature[],
    partner_id: string|null,
    installments: IInstallment[],
    in_use: boolean,

    /** fields not allowed to be copied from other plans and are not editable in UI - we make sure those are not copied at the moment */
    /** currently we count on DB to set default values for them */
    /** SEE removeNotAllowedPlanFields method usage */
    /************************************************************/
    user_id_created_for: null|number,
    start_date: null|string,
    end_date: null|string,
    prototype_plan_start_date: null|string,
    usages_calculation_mechanism: PLAN_CALCULATION_MECHANISMS,
    usages_calculation_mechanism_is_fake: boolean,
    coverage_revenue_share: null|number,
    coverage_label_cost_fixed_margin: null|number,
    coverage_label_cost_relative_margin: null|number,
    ship_by_rgo_label_cost_fixed_margin: null|number,
    ship_by_rgo_label_cost_relative_margin: null|number,
    allowed_days_for_canceling_covered_order: null|number,
    transactions_billing_period: null|string, /** not used field */
    initial_coverage_item_price_usd: null|number,
    is_coverage_price_controlled_by_rgo: null|boolean,
    is_default_coverage_plan: null|boolean,
    /************************************************************/

    _pseudo_type?: PLAN_CHARGE_TYPES,
    _installments_break_down_type?: INSTALLMENTS_BREAKDOWN_TYPES,
}

export enum PLAN_CALCULATION_MECHANISMS {
    DEFAULT = 'DEFAULT',
    TRANSACTIONAL = 'TRANSACTIONS',
    MIXED = 'MIXED'
}

export function removeNotAllowedPlanFields(changes: IPlanEditorModelChanges): IPlanEditorModelChanges {
    const list_forbidded_fields: (keyof IPlanEditorModelChanges)[] = [
        'user_id_created_for',
        'start_date',
        'end_date',
        'prototype_plan_start_date',
        'usages_calculation_mechanism',
        'usages_calculation_mechanism_is_fake',
        'coverage_revenue_share',
        'coverage_label_cost_fixed_margin',
        'coverage_label_cost_relative_margin',
        'ship_by_rgo_label_cost_fixed_margin',
        'ship_by_rgo_label_cost_relative_margin',
        'allowed_days_for_canceling_covered_order',
        'transactions_billing_period',
        'initial_coverage_item_price_usd',
        'is_coverage_price_controlled_by_rgo',
        'is_default_coverage_plan',
    ];
    const cleanedChanges = { ...changes };
    list_forbidded_fields.forEach(field => {
        delete cleanedChanges[field];
    });
    return cleanedChanges;
}

export enum PLAN_TYPES {
    CUSTOM = 'custom',
    REGULAR = 'regular',
    SYSTEM = 'system'
}

export interface IPlanFilters {
    type?: PLAN_TYPES
}

export interface IContactUs {
    plan_description: string
}

export enum FEATURES {
    GIFT_RETURNS = 1,
    MARK_AS_DONE = 2,
    CANCEL_ORDER = 3,
    PACKING_SLIP = 4,
    HELPDESK = 5,
    RETURN_SCORE_RESOLUTION = 6,
    VALIDATE_ITEMS = 7,
    SUPPORTED_LANGUAGES = 8,
    AUTO_EXCHANGE = 9,
    PRODUCT_EXCHANGE = 10,
    AUTO_REFUND = 11,
    WHITE_LABEL = 12,
    VIDEO_UPLOAD = 13,
    ADDONS = 14,
    USERS = 16,
    BXGY = 18,
    RESERVE_ITEMS = 19,
    PORTAL_RMAS = 15,
    ORDER_TRACKING = 20,
    INSTANT_EXCHANGE = 21,
    SWITCH_TO_EXCHANGE = 22,
    ERP_CATEGORY = 23,
    NOTIFICATIONS_CATEGORY = 24,
    PAYMENTS_CATEGORY = 25,
    REFUND_SERVICES_CATEGORY = 26,
    WAREHOUSE_MANAGEMENT_3PL_CATEGORY = 27,
    SHIPPING_CATEGORY_FEATURE = 28,
    PORTAL_CHECKOUT = 29,
    COMPARE_AT_PRICE_DISCOUNT = 30,
    MANAGER_APPROVAL_SETTING_FEATURE = 31,
    CANCEL_INACTIVE_RMAS_FEATURE = 32,
    ANY_TO_ANY_EXCHANGES_FEATURE = 33,
    COVERAGE = 35,
    COVERAGE_SHIPPING_CONNECTION = 36,
    SHIP_BY_RETURNGO = 40,
    GLOBAL_E_SSO = 42,
    SBR_DPD_SERVICE_POINT = 43,

    /** beta features */
    BETA_VALIDATION_RESTOCK_TRIGGER_AUTOMATION = 100009,
    BETA_EMAIL_TEMPLATE_EDITOR = 100036,
    BETA_EMAIL_RETURNS_TEMPLATE_EDITOR = 100041,
    BETA_TRACKING_REPORT = 100048,
    BETA_TRACKING_DB_REPORT = 100065,
    BETA_SHOW_RMA_SCORE_ON_RETURNS_PAGE = 100074,
    BETA_SUPPORT_MULTIPLE_SHIPMENTS_FEATURE = 100075,
    BETA_CANCEL_SHIPMENT_LABEL = 100093,
    BETA_PRE_AUTHORIZATION = 100094,
    BETA_ROUNDED_LABEL_FEE_COST = 100099,
    BETA_PAYMENT_STATUSES_FITLER_IN_MANAGE_RETURNS = 100114,
    BETA_RMS_API_MANAGE_RETURNS = 100124,
    BETA_RUNTIME_ERROR_CLASSIFICATION_DISPLAY = 100128,
    BETA_EXCHANGE_OF_EXCHANGED_ITEMS = 100130,
}

export const FEATURES_WITH_QUOTAS: Record<number, { defaultQuota: number }> = {
    [FEATURES.USERS]: { defaultQuota: 1 }
}

/** MAP ONLY NOT BETA FEATURES HERE, otherwise they will be added to new plans by mistake */
export const NOT_BETA_FEATURES_MAP: Record<number, { label: string }> = {
    [FEATURES.GIFT_RETURNS]: {label: 'Gift returns'},
    [FEATURES.MARK_AS_DONE]: {label: 'Mark as done'},
    [FEATURES.CANCEL_ORDER]: {label: 'Cancel order'},
    [FEATURES.PACKING_SLIP]: {label: 'Packing slip'},
    [FEATURES.HELPDESK]: {label: 'Help desk'},
    [FEATURES.ERP_CATEGORY]: {label: 'ERP'},
    [FEATURES.NOTIFICATIONS_CATEGORY]: {label: 'Notifications'},
    [FEATURES.PAYMENTS_CATEGORY]: {label: 'Payments'},
    [FEATURES.REFUND_SERVICES_CATEGORY]: {label: 'Refund services'},
    [FEATURES.WAREHOUSE_MANAGEMENT_3PL_CATEGORY]: {label: 'WMS/3PL'},
    [FEATURES.SHIPPING_CATEGORY_FEATURE]: {label: 'Shipping'},
    [FEATURES.RETURN_SCORE_RESOLUTION]: {label: 'Return score resolutions'},
    [FEATURES.VALIDATE_ITEMS]: {label: 'Validate items'},
    [FEATURES.SUPPORTED_LANGUAGES]: {label: 'Supported languages'},
    [FEATURES.AUTO_EXCHANGE]: {label: 'Auto-exchange'},
    [FEATURES.PRODUCT_EXCHANGE]: {label: 'Product exchange'},
    [FEATURES.AUTO_REFUND]: {label: 'Auto refund'},
    [FEATURES.WHITE_LABEL]: {label: 'White label'},
    [FEATURES.VIDEO_UPLOAD]: {label: 'Video upload'},
    [FEATURES.ADDONS]: {label: 'Add-ons'},
    [FEATURES.USERS]: {label: 'Users'},
    [FEATURES.BXGY]: {label: 'BXGY'},
    [FEATURES.PORTAL_RMAS]: { label: 'Portal RMAs' },
    [FEATURES.RESERVE_ITEMS]: { label: 'Reserve Items' },
    [FEATURES.ORDER_TRACKING]: {label: 'Order tracking'},
    [FEATURES.INSTANT_EXCHANGE]: {label: 'Instant Exchange'},
    [FEATURES.SWITCH_TO_EXCHANGE]: {label: 'Switch to Exchange'},
    [FEATURES.PORTAL_CHECKOUT]: {label: 'Portal Checkout'},
    [FEATURES.COMPARE_AT_PRICE_DISCOUNT]: {label: 'Compare-at-Price discount'},
    [FEATURES.MANAGER_APPROVAL_SETTING_FEATURE]: {label: 'Manager approval required'},
    [FEATURES.CANCEL_INACTIVE_RMAS_FEATURE]: { label: 'Cancel Inactive RMAs' },
    [FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE]: { label: 'Any to Any Exchanges: Add to Cart' },
    [FEATURES.SHIP_BY_RETURNGO]: { label: 'Ship By ReturnGo' },
    [FEATURES.GLOBAL_E_SSO]: { label: 'Global-E SSO' },
    [FEATURES.SBR_DPD_SERVICE_POINT]: { label: 'SBR - DPD Service point' },
}

/** MAP ONLY BETA FEATURES HERE, if there is intention to allow to add them to new plans */
export const AVAILABLE_BETA_FEATURES_MAP: Record<number, { label: string }> = {
    /** 
     * In some cases we will need to add ability to allow some BETA features to be added to plans, 
     * To do this - just add a key->object values by exaple from NOT_BETA_FEATURES_MAP.
     * If this object will be empty - Available BETA Features will not be shown in plan editor.
     * */
}


export interface IPlanFeature {
    id?: number,
    feature_id: FEATURES,
    plan_id?: number,
    feature_price?: number,
    is_quota_based?: boolean,
    quota: number|null,
    price_per_excess_usage: number|null,
    created_at?: string,
    updated_at?: string,
    _isEnabled?: boolean,
}

export interface IAddonFeature {
    feature_price: number,
    feature_id: number
}
